import AppUpdateWarning from "@/Components/UI/Modals/AppUpdateWarning";
import { AiDraftProvider } from "@/Contexts/AiDraftProvider";
import { AnalyticsProvider } from "@/Contexts/AnalyticsProvider";
import { ChatProvider } from "@/Contexts/ChatProvider";
import { ClientExercisesProvider } from "@/Contexts/ClientExercisesProvider";
import { ContactUsProvider } from "@/Contexts/ContactUsProvider";
import { CookieManagementProvider } from "@/Contexts/CookieManagementProvider";
import { ExercisePickerProvider } from "@/Contexts/ExercisePickerProvider";
import { ExercisesSeenProvider } from "@/Contexts/ExercisesSeenProvider";
import { FollowupTaskProvider } from "@/Contexts/FollowupTaskProvider";
import { GiftCoachingProvider } from "@/Contexts/GiftCoachingProvider";
import { ModalManagerProvider } from "@/Contexts/ModalManagerProvider";
import { PayUsModalsProvider } from "@/Contexts/PayUsModalsProvider";
import { ShareFeedbackProvider } from "@/Contexts/ShareFeedbackProvider";
import { TrainingsProvider } from "@/Contexts/TrainingsProvider";
import { listenToAppEvent, sendEventToApp } from "@/utils/appCommunication";
import { MobileAppTracking } from "@/utils/MobileAppTracking";
import useIntercomMessenger from "@/utils/useIntercomMessenger";
import { ErrorBoundary } from "@sentry/react";
import { PropsWithChildren, useEffect } from "react";
import { BoardAccessProvider } from "./BoardAccessProvider";
import { CoachSidebarDataProvider } from "./CoachSidebarDataProvider";

/**
 * Global state providers do not refresh between Inertia page loads. This is
 * great if you want a component to persist between loads, but this means
 * Inertia props will not automatically refresh.
 *
 * Use this if you want to avoid unnecessary component remounts and data fetches
 * on page navigations
 *
 * Don't use this if you need your component to refresh on navigation, or you
 * don't understand the consequences of using this
 */
export function GlobalStateProviders({ children }: PropsWithChildren) {
    useIntercomMessenger();

    useEffect(() => {
        sendEventToApp("appInitialized", {});
        const unsubscribe = listenToAppEvent("webViewAliveCheck", () => {
            sendEventToApp("isAlive", true);
        });
        return () => unsubscribe();
    }, []);

    return (
        <ErrorBoundary
            fallback={<></>}
            onError={() => {
                if (!import.meta.env.DEV) {
                    window.location.href = route("error", {
                        error_code: 500,
                    });
                }
            }}
        >
            <AppUpdateWarning>
                <ModalManagerProvider>
                    <CookieManagementProvider>
                        <AnalyticsProvider>
                            <MobileAppTracking>
                                <PayUsModalsProvider>
                                    <ChatProvider>
                                        <CoachSidebarDataProvider>
                                            <FollowupTaskProvider>
                                                <ExercisesSeenProvider>
                                                    <TrainingsProvider>
                                                        <GiftCoachingProvider>
                                                            <ShareFeedbackProvider>
                                                                <ContactUsProvider>
                                                                    <ClientExercisesProvider>
                                                                        <BoardAccessProvider>
                                                                            <ExercisePickerProvider>
                                                                                <AiDraftProvider>
                                                                                    {
                                                                                        children
                                                                                    }
                                                                                </AiDraftProvider>
                                                                            </ExercisePickerProvider>
                                                                        </BoardAccessProvider>
                                                                    </ClientExercisesProvider>
                                                                </ContactUsProvider>
                                                            </ShareFeedbackProvider>
                                                        </GiftCoachingProvider>
                                                    </TrainingsProvider>
                                                </ExercisesSeenProvider>
                                            </FollowupTaskProvider>
                                        </CoachSidebarDataProvider>
                                    </ChatProvider>
                                </PayUsModalsProvider>
                            </MobileAppTracking>
                        </AnalyticsProvider>
                    </CookieManagementProvider>
                </ModalManagerProvider>
            </AppUpdateWarning>
        </ErrorBoundary>
    );
}
